// @flow

import * as React from 'react';

import type { FormApi } from 'final-form';
import { useDispatch } from 'react-redux';

import { selectCartIsWorking, selectProductsByType } from '@gandi/cart';
import { Form } from '@gandi/form/FinalForm';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import {
  Alert,
  Base,
  Block,
  BlockButtons,
  BlockHeading,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
} from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import {
  useProductsFetch,
  preFetch,
} from '../../modules/hooks/useProductsFetch';
import { useUpsertCartMailboxes } from '../../modules/hooks/useUpsertCartMailboxes';

import { DurationField } from './components/DurationField';
import { OfferField } from './components/OfferField';

type FormValues = {
  autorenew: string,
  config_name: string,
  duration_month: string,
};

const FORM_ID = 'mailbox-standalone-create';
const productType = { productType: 'mailbox-standalone' };

export function MailboxStandaloneForm(): React.MixedElement {
  const translator = useTranslator();
  const { __ } = translator;
  const dispatch = useDispatch();

  const cartProducts = useAppSelector(state =>
    selectProductsByType(state, productType),
  );

  const { isLoaded: isProductsLoaded, isFailure: isProductsFailure } =
    useProductsFetch();

  const [open, setOpen] = React.useState(!cartProducts.length);

  const isCartWorking = useAppSelector(selectCartIsWorking);

  const { handleUpsertCartMailboxes } = useUpsertCartMailboxes();

  async function handleSubmit(
    { config_name, duration_month }: FormValues,
    form: FormApi<FormValues>,
  ) {
    const payload = {
      config_name,
      duration_month: Number(duration_month),
    };
    const alreadyAddedProduct = cartProducts.find(
      product =>
        product.payload.config_name === payload.config_name &&
        product.payload.duration_month === payload.duration_month,
    );

    try {
      await handleUpsertCartMailboxes(
        {
          ...payload,
          quantity: 1 + (alreadyAddedProduct?.payload.quantity ?? 0),
          autorenew: alreadyAddedProduct?.payload.autorenew ?? true,
        },
        alreadyAddedProduct?.id,
      );
      setOpen(false);
      form.reset();
    } catch (err) {
      dispatch(
        notifyDanger({
          message:
            err.response?.data.message ||
            __('We are unable to add mailboxes to your cart.'),
        }),
      );
    }
  }

  return (
    <Block>
      <BlockHeading>{__('Mailbox creation')}</BlockHeading>
      {isProductsFailure && (
        <Alert
          intent="warning"
          withBackground
          message={__('An error has occurred. You can try to reload the page.')}
          marginBottom="base"
        />
      )}
      <Disclosure open={open} onOpenChange={setOpen}>
        {!open && (
          <DisclosureTrigger
            as={Button}
            iconBefore="plus"
            kind="outline"
            style={{ borderStyle: 'dashed', width: '100%' }}
          >
            {__('Add an other offer')}
          </DisclosureTrigger>
        )}
        <DisclosureContent>
          <Form id={FORM_ID} onSubmit={handleSubmit}>
            <Base marginBottom="base">
              <OfferField />
            </Base>
            <Base marginBottom="base">
              <DurationField />
            </Base>
          </Form>
        </DisclosureContent>
      </Disclosure>
      {!!open && (
        <BlockButtons flexJustifyBetween>
          <Button
            kind="outline"
            onClick={() => {
              setOpen(false);
            }}
            small
            type="button"
          >
            {__('Cancel')}
          </Button>
          <Button
            form={FORM_ID}
            loading={isCartWorking || (!isProductsLoaded && !isProductsFailure)}
            disabled={isProductsFailure}
            small
          >
            {__('Add to cart')}
          </Button>
        </BlockButtons>
      )}
    </Block>
  );
}

MailboxStandaloneForm.preFetch = preFetch;
